import type { IDownload } from "../office/OfficeDownload";
import { ProgressiveDebounce } from "./ProgressiveDebounce";

export const GATE_UPLOAD_ABORT_DURING_SCAN = true;

export type PipelinePhase = "unregistered" | "idle" | "debouncing" | "downloading" | "uploading" | "error" | "stopped";

export interface PipelineOptions {
  debounceMs: number;
  debounceMultiplier?: number;
  download: IDownload;
  onReady: (file: File, signal: AbortSignal) => void;
}

/**
 * Manages document update lifecycle with debouncing, download/upload phases, and cancellation support.
 * 
 * The pipeline transitions through phases in response to document modifications:
 * - `idle` → `debouncing` → `downloading` → `uploading` → `idle`
 * - Can transition to `error` on failures or `stopped` when paused.
 * 
 * Must call `register()` before the pipeline can process modifications.
 */
export class UpdatePipeline {
  private _phase: PipelinePhase = "unregistered";
  private _abort: AbortController | null = null;
  private _debounce: ProgressiveDebounce | null = null;
  private _ignoreNext: boolean = false;
  private _listeners: Set<(phase: PipelinePhase, prev: PipelinePhase) => void> = new Set();
  private _updateResolve: (() => void) | null = null;
  private _updateReject: ((error: Error) => void) | null = null;
  private _options: PipelineOptions | null = null;
  private _runStartTime: number = 0;
  private _hasPendingUploadUpdate: boolean = false;

  constructor() { }

  /**
   * Registers the pipeline with the required options.
   * Transitions the pipeline from "unregistered" to "idle" phase.
   * Must be called before the pipeline can process modifications.
   */
  register(options: PipelineOptions): void {
    if (this._phase !== "unregistered") {
      throw new Error(`Cannot register from phase: ${this._phase}`);
    }
    this._options = options;
    this._debounce = new ProgressiveDebounce(options.debounceMs, options.debounceMultiplier ?? 2);
    console.log("[UpdatePipeline] Registered with debounce:", options.debounceMs, "multiplier:", options.debounceMultiplier ?? 2);
    this.transition("idle");
  }

  /** Current phase of the pipeline. */
  get phase(): PipelinePhase {
    return this._phase;
  }

  /** AbortSignal for the current operation, or null if idle. */
  get signal(): AbortSignal | null {
    return this._abort?.signal ?? null;
  }

  /**
   * Called when a document modification is detected.
   * Starts or resets the debounce timer before triggering a download.
   * If already downloading or uploading, aborts the current operation first.
   * No-op if the pipeline is stopped.
   * 
   * @throws Error if not registered
   */
  onModified(): void {
    if (this._phase === "unregistered") {
      throw new Error(`Cannot call onModified from phase: ${this._phase}`);
    }

    if (this._phase === "stopped") {
      console.log("[UpdatePipeline] onModified ignored, pipeline stopped");
      return;
    }

    if (this._ignoreNext) {
      this._ignoreNext = false;
      console.log("[UpdatePipeline] onModified ignored (ignoreNext flag)");
      return;
    }

    console.log("[UpdatePipeline] onModified, current phase:", this._phase);

    if (this._phase === "uploading" && GATE_UPLOAD_ABORT_DURING_SCAN) {
      this.queuePendingUploadUpdate();
      return;
    }

    if (this._phase === "downloading" || this._phase === "uploading") {
      this.abortCurrent();
    }

    this.transition("debouncing");

    this._debounce!.schedule(() => {
      this.startRun();
    });
  }

  /**
   * Cancels any pending or in-progress operation and returns to idle.
   * No-op if already idle.
   * 
   * @throws Error if not registered
   */
  cancel(): void {
    if (this._phase === "unregistered") {
      throw new Error(`Cannot call cancel from phase: ${this._phase}`);
    }

    if (this._phase === "idle") {
      console.log("[UpdatePipeline] Cancel ignored, already idle");
      return;
    }

    console.log("[UpdatePipeline] Cancelling from:", this._phase);
    this.clearDebounce();
    this._hasPendingUploadUpdate = false;
    this.abortCurrent();
    this.transition("idle");
  }

  /**
   * Stops the pipeline completely. Cancels any operation and ignores future onModified calls
   * until resume() is called.
   * 
   * @throws Error if not registered
   */
  stop(): void {
    if (this._phase === "unregistered") {
      throw new Error(`Cannot call stop from phase: ${this._phase}`);
    }

    console.log("[UpdatePipeline] Stopping from:", this._phase);
    this.clearDebounce();
    this._hasPendingUploadUpdate = false;
    this.abortCurrent();
    this.transition("stopped");
  }

  /** Resumes the pipeline from stopped state to idle. No-op if not stopped. */
  resume(): void {
    if (this._phase !== "stopped") {
      console.log("[UpdatePipeline] Resume ignored, not stopped (phase:", this._phase, ")");
      return;
    }
    console.log("[UpdatePipeline] Resuming");
    this.transition("idle");
  }

  /**
   * Ignores the next onModified call. Used to prevent the pipeline from reacting
   * to programmatic changes that shouldn't trigger an update.
   * 
   * @throws Error if not registered
   */
  ignoreNextModification(): void {
    if (this._phase === "unregistered") {
      throw new Error(`Cannot call ignoreNextModification from phase: ${this._phase}`);
    }
    console.log("[UpdatePipeline] Will ignore next modification");
    this._ignoreNext = true;
  }

  /** Called when upload completes successfully. Transitions from uploading to idle. */
  onUploadComplete(): void {
    if (this._phase !== "uploading") {
      return;
    }

    const elapsed = Date.now() - this._runStartTime;
    this._debounce!.recordExecution(elapsed);
    console.log("[UpdatePipeline] onUploadComplete, elapsed:", elapsed, "ms, next debounce:", this._debounce!.getCurrentDebounce(), "ms");

    this._abort = null;

    if (this._hasPendingUploadUpdate) {
      this.startPendingUploadUpdate();
      return;
    }

    this.transition("idle");
    this.resolvePending();
  }

  /** Called when upload fails. Transitions from uploading to error. */
  onUploadError(error: Error): void {
    if (this._phase !== "uploading") {
      return;
    }

    console.error("[UpdatePipeline] Upload error:", error);
    this._abort = null;
    this._hasPendingUploadUpdate = false;
    this.transition("error");
    this.rejectPending(error);
  }

  /**
   * Registers a callback to be notified on phase transitions.
   * @returns Unsubscribe function.
   */
  onChange(cb: (phase: PipelinePhase, prev: PipelinePhase) => void): () => void {
    this._listeners.add(cb);
    return () => {
      this._listeners.delete(cb);
    };
  }

  /**
   * Triggers an immediate update without debounce.
   * Aborts any current operation and starts download immediately.
   * No-op if the pipeline is stopped.
   * @returns Promise that resolves when download completes and upload starts.
   * @throws Error if not registered
   */
  update(): Promise<void> {
    if (this._phase === "unregistered") {
      throw new Error(`Cannot call update from phase: ${this._phase}`);
    }

    if (this._phase === "stopped") {
      console.log("[UpdatePipeline] Update ignored, pipeline stopped");
      return Promise.resolve();
    }

    console.log("[UpdatePipeline] Immediate update requested");

    if (this._phase === "uploading" && GATE_UPLOAD_ABORT_DURING_SCAN) {
      this.queuePendingUploadUpdate();
      return new Promise<void>((resolve, reject) => {
        this._updateResolve = resolve;
        this._updateReject = reject;
      });
    }

    this.clearDebounce();

    if (this._phase === "downloading" || this._phase === "uploading") {
      this.abortCurrent();
    }

    return new Promise<void>(async (resolve, reject) => {
      this._updateResolve = resolve;
      this._updateReject = reject;
      await this.startRun();
    });
  }

  private transition(phase: PipelinePhase): void {
    const prev = this._phase;
    this._phase = phase;
    console.log("[UpdatePipeline] Phase:", prev, "->", phase);

    for (const listener of this._listeners) {
      listener(phase, prev);
    }
  }

  private async startRun(): Promise<void> {
    console.log("[UpdatePipeline] Starting run");
    this._runStartTime = Date.now();
    const abort = new AbortController();
    this._abort = abort;
    this.transition("downloading");

    try {
      const file = await this._options!.download.start(abort.signal);

      if (abort.signal.aborted) {
        console.log("[UpdatePipeline] Download aborted, result discarded");
        return;
      }

      console.log("[UpdatePipeline] Download complete, starting upload");
      this.transition("uploading");
      this._options!.onReady(file, abort.signal);
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") {
        return;
      }

      console.error("[UpdatePipeline] Download error:", error);
      this._abort = null;
      this.transition("error");
      this.rejectPending(error instanceof Error ? error : new Error(String(error)));
    }
  }

  private queuePendingUploadUpdate(): void {
    console.log("[UpdatePipeline] Upload in progress, queueing pending update");
    this._hasPendingUploadUpdate = true;
  }

  private startPendingUploadUpdate(): void {
    this._hasPendingUploadUpdate = false;
    this.transition("idle");
    this.onModified();
  }

  private clearDebounce(): void {
    if (this._debounce) {
      console.log("[UpdatePipeline] Debounce timer cleared");
      this._debounce.clear();
    }
  }

  private abortCurrent(): void {
    if (this._abort) {
      console.log("[UpdatePipeline] Aborting current operation");
      this._abort.abort();
      this._abort = null;
    }
    this.rejectPending(new Error("Update was aborted"));
  }

  private resolvePending(): void {
    if (this._updateResolve) {
      this._updateResolve();
      this._updateResolve = null;
      this._updateReject = null;
    }
  }

  private rejectPending(error: Error): void {
    if (this._updateReject) {
      this._updateReject(error);
      this._updateResolve = null;
      this._updateReject = null;
    }
  }
}
